import {useRef, useState} from 'react';
import {getApiErrorMessage} from '@/api/client';
import {itemsApi, type Item, type ResolveItemRequest} from '@/api/items';
import {type FlagColor} from '@/api/flags';
import {showToast} from '@/lib/toast';

type Flags = Partial<Record<string, FlagColor>>;

// What the server needs to make a page-built Item a real one - the same fields Classify would send.
function toResolveRequest(item: Item): ResolveItemRequest {
  return {
    item_id: item.id,
    external_id: item.external_id,
    url: item.url,
    title: item.title,
    thumbnail_url: item.thumbnail_url,
    published_at: item.published_at,
    author_id: item.author_id,
    author_name: item.author_name,
    author_handle: item.author_handle,
    metadata: item.metadata,
    affiliate: item.affiliate,
    platform: item.platform,
    submission_type: item.content_type === 'actor' ? 'Actor' : 'Content'
  };
}

// Flags an analyst picks on a submission page, where the rows are looked-up videos, channels or pins
// with no item behind them. Flagging one makes it a real item first (itemsApi.resolve) and saves the
// flag straight away, so it is in My flagged items whether or not it is ever submitted. Keyed by the
// page's own row key; the resolved item id is remembered per key so a later Classify of the same row
// lands on the same item. `buildItem` rebuilds a row's Item from its key (null if the row is gone).
export function usePendingFlags(buildItem: (key: string) => Item | null) {
  const [flags, setFlags] = useState<Flags>({});
  const [notes, setNotes] = useState<Partial<Record<string, string>>>({});
  const itemIds = useRef(new Map<string, string>());
  // Writes go to the server one after another, so a flag followed quickly by a clear (or another
  // colour) lands in the order the analyst clicked rather than whichever request wins the race.
  const queue = useRef<Promise<void>>(Promise.resolve());

  const resolveId = async (key: string): Promise<string | null> => {
    const known = itemIds.current.get(key);
    if (known) {
      return known;
    }

    const item = buildItem(key);
    if (!item) {
      return null;
    }

    const {item_id: id} = await itemsApi.resolve(toResolveRequest(item));
    itemIds.current.set(key, id);
    return id;
  };

  const setMany = (keys: readonly string[], color: FlagColor | null, note?: string | null) => {
    const previous = flags;
    const previousNotes = notes;
    setFlags((current) => {
      const next = {...current};
      for (const key of keys) {
        if (color) {
          next[key] = color;
        } else {
          Reflect.deleteProperty(next, key);
        }
      }

      return next;
    });
    // A colour change keeps the row's note (the server does too); clearing the flag drops it.
    setNotes((current) => {
      const next = {...current};
      for (const key of keys) {
        if (!color) {
          Reflect.deleteProperty(next, key);
        } else if (note) {
          next[key] = note;
        }
      }

      return next;
    });

    queue.current = queue.current.then(async () => {
      try {
        // Clearing only touches rows that were ever saved; the rest have nothing to clear.
        const resolved = await Promise.all(
          keys.map(async (key) => (color ? resolveId(key) : (itemIds.current.get(key) ?? null)))
        );
        const ids = resolved.filter((id): id is string => id !== null);
        if (ids.length > 0) {
          await itemsApi.setFlags(ids, color, note);
        }
      } catch (error) {
        // Put the rows back as they were: a flag that isn't saved must not look as if it is.
        setFlags((current) => {
          const next = {...current};
          for (const key of keys) {
            const before = previous[key];
            if (before) {
              next[key] = before;
            } else {
              Reflect.deleteProperty(next, key);
            }
          }

          return next;
        });
        setNotes(previousNotes);
        showToast(getApiErrorMessage(error, 'Failed to save flag.'), 'error');
      }
    });
  };

  // Edits the note on a row that is already flagged (so already a saved item).
  const setNote = (key: string, note: string | null) => {
    const previousNotes = notes;
    setNotes((current) => {
      const next = {...current};
      if (note) {
        next[key] = note;
      } else {
        Reflect.deleteProperty(next, key);
      }

      return next;
    });
    queue.current = queue.current.then(async () => {
      const id = itemIds.current.get(key);
      try {
        if (id) {
          await itemsApi.setFlagNote(id, note);
        }
      } catch (error) {
        setNotes(previousNotes);
        showToast(getApiErrorMessage(error, 'Failed to save note.'), 'error');
      }
    });
  };

  // The colour every key shares, or null - a mixed set reads as unflagged, and picking from it
  // overwrites each row's own choice.
  const sharedBy = (keys: readonly string[]): FlagColor | null => {
    const first = keys.length > 0 ? flags[keys[0]!] : undefined;
    return first && keys.every((key) => flags[key] === first) ? first : null;
  };

  return {
    flagOf: (key: string): FlagColor | null => flags[key] ?? null,
    noteOf: (key: string): string | null => notes[key] ?? null,
    setNote,
    setFlag(key: string, color: FlagColor | null, note?: string | null) {
      setMany([key], color, note);
    },
    setAll: setMany,
    sharedBy,
    anyFlagged: (keys: readonly string[]) => keys.some((key) => flags[key]),
    // The item id a row was saved under, if it was flagged - Classify must reuse it.
    itemIdOf: (key: string): string | undefined => itemIds.current.get(key)
  };
}
